import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchAiStaffKnowledge,
  fetchAiStaffMessages,
  fetchAiStaffSessions,
  queryAiStaff,
  recordAiStaffHandoff,
  triggerAiStaffReindex,
  type AiStaffConversationMessage,
  type AiStaffConversationSession,
  type AiStaffHandoffRecord,
  type AiStaffKnowledgeChunk,
  type AiStaffQuotaEvaluation,
  type AiStaffReindexResult,
  type AiStaffUsageMetrics,
  type QueryAiStaffPayload,
  type RecordAiStaffHandoffPayload,
} from '@/lib/api/aiStaff';

type SessionsQueryParams = {
  status?: AiStaffConversationSession['status'];
  limit?: number;
};

export const useAiStaffSessions = (
  params: SessionsQueryParams = {},
): UseQueryResult<AiStaffConversationSession[]> => {
  const { status, limit } = params;

  return useQuery({
    queryKey: ['ai-staff', 'sessions', status ?? 'all', limit ?? 20],
    staleTime: 10_000,
    queryFn: ({ signal }) =>
      fetchAiStaffSessions(
        {
          status,
          limit,
        },
        { signal },
      ),
  });
};

export const useAiStaffMessages = (
  sessionId: string | null,
  params: { limit?: number } = {},
): UseQueryResult<AiStaffConversationMessage[]> => {
  const enabled = Boolean(sessionId);
  const { limit } = params;

  return useQuery({
    queryKey: ['ai-staff', 'messages', sessionId ?? 'none', limit ?? 50],
    enabled,
    staleTime: 5_000,
    queryFn: ({ signal }) => {
      if (!sessionId) {
        return Promise.resolve([]);
      }
      return fetchAiStaffMessages(sessionId, { limit }, { signal });
    },
  });
};

export const useAiStaffKnowledge = (
  params: { limit?: number; visibility?: AiStaffKnowledgeChunk['visibility']; locale?: string } = {},
): UseQueryResult<AiStaffKnowledgeChunk[]> => {
  const { limit = 10, visibility = 'staff', locale } = params;

  return useQuery({
    queryKey: ['ai-staff', 'knowledge', visibility, limit, locale ?? 'all'],
    staleTime: 60_000,
    queryFn: ({ signal }) =>
      fetchAiStaffKnowledge(
        {
          limit,
          visibility,
          locale,
        },
        { signal },
      ),
  });
};

type AiStaffQueryResult = {
  conversationId: string;
  response: string;
  finishReason?: string | null;
  quota: AiStaffQuotaEvaluation;
  usage: AiStaffUsageMetrics;
  messages: AiStaffConversationMessage[];
};

export const useAiStaffQuery = (): UseMutationResult<
  AiStaffQueryResult,
  unknown,
  QueryAiStaffPayload
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: QueryAiStaffPayload) => queryAiStaff(payload),
    onSuccess: (data) => {
      queryClient.setQueryData<AiStaffConversationMessage[]>(
        ['ai-staff', 'messages', data.conversationId, 50],
        data.messages,
      );
      void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'messages', data.conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'sessions'] });
    },
  });
};

export const useAiStaffReindex = (): UseMutationResult<
  AiStaffReindexResult,
  unknown,
  { locationId?: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { locationId?: string }) => triggerAiStaffReindex(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'knowledge'] });
    },
  });
};

export const useAiStaffHandoff = (
  sessionId: string | null,
): UseMutationResult<
  AiStaffHandoffRecord,
  unknown,
  RecordAiStaffHandoffPayload
> => {
  return useMutation({
    mutationFn: async (input: RecordAiStaffHandoffPayload) => {
      if (!sessionId) {
        throw new Error('Session ID is required to record a handoff.');
      }
      return recordAiStaffHandoff(sessionId, input);
    },
  });
};
